<template>
    <div>
        <h2 class="text-center">Sites</h2>

        <!-- Search Bar -->
        <div class="flex flex-col justify-center mb-2">
            <div class="flex items-center">
                <label class="text-base pr-2" for="search">Search</label>
                <input class="grow md:grow-0 md:w-1/3 rounded-md border-2 border-solid border-gray-100 shadow-sm py-2 px-4"
                       type="text" id="search" name="search" v-model="search" ref="search">
            </div>
        </div>

        <!-- General Loading Panels -->
        <div    v-if="searchNeeded"
                class="">
            <div    v-if="loading"
                    class="flex items-center">
                <span :class="iconLoading"></span>
                <span class="">Searching</span>
            </div>
            <div    v-else>
                <span>Search for a Company.</span>
            </div>
        </div>
        <div    v-else-if="loading">
            <div     v-for="i in 4"
                     class="py-4 flex space-x-3 bg-white shadow px-8 py-2 my-4 sm:rounded-lg sm:p-6">
                <div class="flex flex-col w-full">
                    <loading width="w-1/5" height="h-8" class="mb-4"></loading>
                    <loading width="w-2/3" height="h-8" class="mb-4"></loading>
                </div>
            </div>
        </div>
        <div    v-else-if="data.length === 0">
            <div    class="">
                No Matching Companies Found.
            </div>
        </div>
        <div    v-else>
            <!-- This example requires Tailwind CSS v2.0+ -->
            <div class="flex flex-col">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
<!--                            <a class="min-w-full divide-y divide-gray-200">-->
                                <div class="hidden md:grid grid-cols-2 md:grid-cols-5 bg-gray-50">
                                    <div class="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Site
                                    </div>
                                    <div class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Threshold Alarms
                                    </div>
                                    <div class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Offline Alarms
                                    </div>
                                    <div v-if="permissions.can('view_snapshot')" class="hidden md:block px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Snapshot Issues
                                    </div>
                                </div>

                                <!-- Row -->
                            <enterprise-dashboard-row v-for="(company, index) in data"
                                                      :company-id="index"
                                                      :company="company"
                                                      :loaded="company.loaded"
                                                      :largest-depth="largestDepth"
                                                      :can-view-snapshot="permissions.can('view_snapshot', index)"
                                                      :is-sonicu="permissions.isSonicu()"
                                                      :should-show="company.shouldShow"
                                                      :demo-hide="company.demoHide"
                                                      >
                            </enterprise-dashboard-row>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import AlarmStatus from "@/components/EnterpriseDashboard/AlarmStatus"
import PointStatus from '@/components/EnterpriseDashboard/PointStatus'
import SnapshotIssues from "@/components/EnterpriseDashboard/SnapshotIssues"
import WizardStatus from '@/components/EnterpriseDashboard/WizardStatus'

import { SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import { iconNamesMixin } from "@/mixins/IconNamesMixin";
import axios from "axios";
import EnterpriseDashboardRow from "@/components/EnterpriseDashboard/EnterpriseDashboardRow.vue";
import {UrlParamsMixin} from "../../mixins/UrlParamsMixin";

const SEARCH_DELAY = 250

// Company summaries are loaded in batches through a bounded worker pool so a
// large enterprise account fires ceil(N / BATCH_SIZE) requests (CONCURRENCY at
// a time) instead of one request per company. Mirrors the Enterprise Alarm
// Report loader. The batch endpoint caps at 25 companies per request.
const BATCH_SIZE = 10
const CONCURRENCY = 2

export default {
    name: "EnterpriseDashboard",
    mixins: [iconNamesMixin, SonicuCoreMixin, UrlParamsMixin],
    components: {EnterpriseDashboardRow, AlarmStatus, PointStatus, SnapshotIssues, WizardStatus},
    props: {
        companyId: String,
    },
    data() {
        return {
            data: {},
            loading: true,
            searchNeeded: false,
            search: '',
            lastLoadSearch: '',
            fLoadData: null,
            currentRequest: 0,
            pendingRequestCancelSources: [],
            isLeavingPage: false,

            firstLoad: true,
            demoMode: false,
        }
    },
    created() {
        // Only initialize demo mode here on the enterprise dashboard
        this.demoMode = localStorage.getItem('enterprise_demo_mode') === 'true'
        window.addEventListener('demo-mode-changed', this.onDemoModeChanged)
        this.loadFunction = _.debounce(() => {
            // companyId: Company Name
            let url = (this.companyId !== '') ? `${this.companyId}/`  : ''
            this.cancelPendingRequests()
            this.data = {}
            this.loading = true
            this.lastLoadSearch = this.search
            this.currentRequest++
            const rootRequestSource = axios.CancelToken.source()
            this.pendingRequestCancelSources.push(rootRequestSource)
            axios.get(`/api-internal/${url}enterprise-dashboard?search=${this.search}&request=${this.currentRequest}`, {
                cancelToken: rootRequestSource.token,
            }).then(
                response => {
                    if (this.isLeavingPage) {
                        return
                    }
                    // This is to ensure that the only request that is used is the most recent sent, not received.
                    if (parseInt(response.data.request) !== this.currentRequest) {
                        return
                    }
                    let companyInfo = response.data.companies
                    this.loading = false
                    if ('searchNeeded' in response.data) {
                        this.searchNeeded = true
                        return
                    }
                    this.data = companyInfo
                    this.applyDemoFilter()
                    // Mark rows loaded/visible up front, then collect the site-backed
                    // company ids to pull summaries for in batches.
                    const summaryQueue = []
                    Object.keys(this.data).forEach(index => {
                        let companyInfo = this.data[index]
                        companyInfo.loaded = !companyInfo.has_site
                        companyInfo.shouldShow = this.search === '' || _.isNil(this.search)
                        this.data[index] = companyInfo
                        if (companyInfo['has_site']) {
                            summaryQueue.push(companyInfo.id)
                        }
                    })
                    this.loadCompanySummaries(summaryQueue, this.currentRequest)
                    this.updateShouldShow()
                    this.searchNeeded = false
                }
            ).catch(error => {
                if (!axios.isCancel(error)) {
                    console.error(error)
                    this.loading = false
                }
            }).finally(() => {
                this.pendingRequestCancelSources = this.pendingRequestCancelSources.filter(source => source !== rootRequestSource)
            }).then(() => {
                // If this is the first load of the page, update the search after we have gotten first results
                if (this.search === '' && this.firstLoad) {
                    this.search = this.urlParamsGet('search', '')
                }

                this.firstLoad = false
            })
        }, SEARCH_DELAY)
    },
    mounted() {
        this.$refs.search.focus();
        window.addEventListener('beforeunload', this.onBeforeUnload)
        window.addEventListener('pagehide', this.onBeforeUnload)
        this.loadData()
    },
    methods: {
        loadData() {
            this.loadFunction()
        },
        // Load company summaries through a bounded worker pool: chunk the ids
        // into batches, then run CONCURRENCY workers that each pull a batch off
        // the shared queue. Merges each batch's summaries into this.data as it
        // arrives, so rows fill in incrementally. requestId guards against a
        // superseded search overwriting fresher data.
        async loadCompanySummaries(ids, requestId) {
            // The batch endpoint's {company_id} segment is only used for routing/
            // middleware (the controller authorizes each child id itself). On the
            // global dashboard companyId is '', so fall back to the sonicu root.
            const root = this.companyId || 'sonicu'
            const batches = []
            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                batches.push(ids.slice(i, i + BATCH_SIZE))
            }

            const loadBatch = async (batch) => {
                const source = axios.CancelToken.source()
                this.pendingRequestCancelSources.push(source)
                try {
                    const response = await axios.get(
                        `/api-internal/${root}/dashboard/company-summary-batch?companies=${batch.join(',')}&request=${requestId}`,
                        { cancelToken: source.token }
                    )
                    if (this.isLeavingPage || requestId !== this.currentRequest) {
                        return
                    }
                    const summaries = response.data.summaries || {}
                    const failed = response.data.failed || []
                    Object.keys(summaries).forEach(id => {
                        if (!(id in this.data)) {
                            return
                        }
                        const summary = summaries[id]
                        summary.loaded = true
                        summary.depth = this.data[id].depth
                        this.data[id] = summary
                    })
                    // Ids the server couldn't/wouldn't return still need to stop spinning.
                    failed.forEach(id => {
                        if (id in this.data) {
                            this.data[id].loaded = true
                        }
                    })
                    this.applyDemoFilter()
                    if (this.search === '' || _.isNil(this.search)) {
                        Object.keys(summaries).forEach(id => {
                            if (id in this.data) {
                                this.data[id].shouldShow = true
                            }
                        })
                    } else {
                        this.updateShouldShow()
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        console.error(error)
                        // A failed batch shouldn't leave rows spinning forever.
                        batch.forEach(id => {
                            if (id in this.data) {
                                this.data[id].loaded = true
                            }
                        })
                    }
                } finally {
                    this.pendingRequestCancelSources = this.pendingRequestCancelSources.filter(s => s !== source)
                }
            }

            const queue = batches.slice()
            const worker = async () => {
                for (let batch = queue.shift(); batch; batch = queue.shift()) {
                    await loadBatch(batch)
                }
            }
            await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batches.length) }, worker))
        },
        cancelPendingRequests() {
            this.pendingRequestCancelSources.forEach(source => source.cancel('Enterprise dashboard request cancelled'))
            this.pendingRequestCancelSources = []
        },
        alarmsLink(company, alarmType) {
            let url = `/${company.id}/alarms?`
            url += alarmType === 'threshold' ? 'showThreshold=true&showOffline=false&showValidation=false' : 'showThreshold=false&showOffline=true&showValidation=false'
            return url;
        },
        companyName(company) {
            // Provide a company name for non-sonicu users or company name (id) for sonicu users
            let name = ''
            if (this.permissions.isSonicu()) {
                name = `${company['name']} (${company['id']})`
            }
            else {
                name = `${company['name']}`
            }
            return name
        },
        searchInCompany(company) {
            let search = this.search.toLowerCase()
            let id = company.id.toLowerCase()
            let name = company.name.toLowerCase()

            return id.includes(search) || name.includes(search)
        },
        updateShouldShow() {
            if (this.search === '' || _.isNil(this.search)) {
                Object.keys(this.data).forEach(key => {
                    this.data[key].shouldShow = true
                })
                this.applyDemoFilter()
                return
            }

            // Reset all should Shows
            Object.keys(this.data).forEach(key => {
                this.data[key].shouldShow = false
            })

            // Ensure Parents show when a client matches a search.
            // Importantly this could have issues at large client amounts as it has a n^3 time growth.  But I believe this to be fine at the moment.
            Object.keys(this.data).forEach(key => {
                let info = this.data[key]
                if (!('id' in info)) {
                    return
                }

                let shouldShow = this.searchInCompany(info)
                this.data[key].shouldShow = this.data[key].shouldShow || shouldShow


                if (this.data[key].shouldShow) {
                    // Add all the children
                    let clientStack = [key]
                    while (clientStack.length > 0) {
                        let client = clientStack.pop()
                        this.data[client].shouldShow = true

                        Object.keys(this.data).forEach(clientId => {
                            if (this.data[clientId].shouldShow) {
                                return
                            }
                            if (this.data[clientId].parent === key) {
                                clientStack.push(clientId)
                            }
                        })
                    }

                    let parent = this.data[key].parent

                    let done = false
                    while (!done && parent !== 'sonicu') {
                        if (_.isNil(parent) || parent === '') {
                            done = true
                            continue
                        }
                        if (!(parent in this.data)) {
                            done = true
                            continue
                        }
                        this.data[parent]['shouldShow'] = true
                        if (parent === this.companyId) {
                            done = true
                        }
                        parent = this.data[parent].parent
                    }
                }
            })
            this.applyDemoFilter()
        },
        onBeforeUnload() {
            this.isLeavingPage = true
            this.cancelPendingRequests()
        },
        calculateTieringOffset(company) {
            return (company.depth * 3) + 'em'
        },
        toLargestOffset(company) {
            return ((this.largestDepth - company.depth) * 3) + 'em'
        },
        onDemoModeChanged(event) {
            this.demoMode = event.detail
            this.applyDemoFilter()
        },
        applyDemoFilter() {
            Object.keys(this.data).forEach(key => {
                let company = this.data[key]
                let hideByOffline = false
                let hideByNoPoints = false
                // If in wizard mode, let's ensure we don't hide it.
                if(company.siteActive === false){
                    company.demoHide = false
                    return
                }
                if (company.pointSummary) {
                    let total = company.pointSummary.green + company.pointSummary.yellow + company.pointSummary.red + company.pointSummary.offline
                    let ratio = total > 0 ? company.pointSummary.offline / total : 0
                    hideByOffline = ratio > 0.1
                    hideByNoPoints = total === 0
                }
                let hideByStatus = company.suspended
                company.demoHide = this.demoMode && (hideByOffline || hideByStatus || hideByNoPoints)
            })
        },
    },
    computed: {
        largestDepth() {
            let maxIndex = Object.keys(this.data)[0]
            Object.keys(this.data).forEach(index => {
                let item = this.data[index]
                if (!('id' in item)) {
                    return
                }
                if (item.depth > this.data[maxIndex].depth) {
                    maxIndex = index
                }
            })

            return this.data[maxIndex].depth + 1
        },
    },
    watch: {
        search(val, old) {
            // Store the search in the URL if it is not blank
            if (val === '' || _.isNil(val)) {
                this.urlParamsRemove('search')
            }
            else {
                this.urlParamsStore('search', val)
            }

            this.search = val
            if (this.searchNeeded) {
                this.loadData()
            }
            else if (!val.startsWith(this.lastLoadSearch)) {
                this.loadData()
            }
            else {
                this.updateShouldShow()
            }
        }
    },
    beforeUnmount() {
        this.onBeforeUnload()
        window.removeEventListener('beforeunload', this.onBeforeUnload)
        window.removeEventListener('pagehide', this.onBeforeUnload)
        window.removeEventListener('demo-mode-changed', this.onDemoModeChanged)
    },
}

</script>
